import { useState } from 'react';
import { X, Loader2, Plus } from 'lucide-react';
import { API_BASE_URL } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { Resource, ResourceType, ResourceStatus } from '../../types';

const typeOptions: ResourceType[] = ['vehicle', 'equipment', 'personnel', 'facility'];
const statusOptions: ResourceStatus[] = ['available', 'deployed', 'en_route', 'maintenance', 'offline', 'reserved'];
const departmentOptions = ['Fire', 'Police', 'EMS', 'Hazmat', 'Search & Rescue', 'Other'];

interface CreateResourceModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (resource: Resource) => void;
  eventId?: string;
}

export default function CreateResourceModal({ open, onClose, onCreated, eventId }: CreateResourceModalProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<ResourceType>('vehicle');
  const [status, setStatus] = useState<ResourceStatus>('available');
  const [department, setDepartment] = useState('Fire');
  const [capabilities, setCapabilities] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setName('');
    setType('vehicle');
    setStatus('available');
    setDepartment('Fire');
    setCapabilities('');
    setNotes('');
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const capsList = capabilities
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

      const newResource: Resource = {
        id: `R-${String(Date.now()).slice(-4)}`,
        name: name.trim(),
        type,
        status,
        department,
        capabilities: capsList,
        notes: notes.trim() || undefined,
        lastUpdate: new Date().toISOString(),
      };

      // Try to POST to API; fall back to local creation if endpoint doesn't exist
      try {
        const res = await fetch(`${API_BASE_URL}/resources`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ...newResource, eventId }),
        });
        if (res.ok) {
          const data = await res.json();
          onCreated?.(data.resource || newResource);
        } else {
          onCreated?.(newResource);
        }
      } catch {
        onCreated?.(newResource);
      }

      reset();
      onClose();
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" />
            Add New Resource
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="e.g. Engine-7, Medic-1, Drone Alpha"
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as ResourceType)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ResourceStatus)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Capabilities</label>
            <input
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="Comma separated, e.g. pumper, rescue, aerial"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground block mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              placeholder="Optional notes..."
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {creating ? 'Adding...' : 'Add Resource'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
